import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { EventTypology } from "helpers/Enums";

interface ISchedulingContext {
    search: ISearchRequest
    scheduling: IScheduleResponse | null
    isLoading: boolean
    scheduleDetails: IEventInDate | null

    doSearch: (search: ISearchRequest) => void
    searchScheduling: (searchRequest: ISearchRequest) => Promise<void>
    handleChange: (e: ISearchRequest) => void
    openScheduleDetails: (event: IEventInDate, isBusy: boolean) => void
    closeScheduleDetails: () => void
    dateBackHandler: () => void
    dateForwardHandler: () => void
    selectWeek: () => void
    selectMonth: () => void
}

interface ISearchRequest {
    dateFrom: moment.Moment
    dateTo: moment.Moment

    userNames: string[]
    showEmpty: boolean
}


interface IScheduleResponse {
    schedules: ISchedule[]
    dates: moment.Moment[]
}

interface ISchedule {
    username: string
    rowSpan: number
    rows: IScheduleRow[]
}

export interface IScheduleRow {
    cells: IScheduleCell[]
}

export interface IScheduleCell {
    isBusy: boolean
    colSpan: number
    startDate: moment.Moment
    eventDetail: IEventInDate
}

export interface IEventInDate {
    ticketId: string
    absenceId: string
    eventTypology: EventTypology
    ticketNr: string
    title: string
    startDate: moment.Moment
    endDate: moment.Moment
    customerId?: number
    customerName: string
}

export let schedulingContext = React.createContext({} as ISchedulingContext);

let { Provider } = schedulingContext;

let SchedulingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let [isLoading, setLoading] = useState(true);

    let [scheduling, setScheduling] = useState<IScheduleResponse | null>(null);
    let [scheduleDetails, setScheduleDetails] = useState<IEventInDate | null>(null);


    let [search, setSearch] = useState<ISearchRequest>({
        dateFrom: moment().add(-7, 'days').startOf('day').utc(true),
        dateTo: moment().add(+14, 'days').startOf('day').utc(true),
        userNames: [],
        showEmpty: false
    });


    useEffect(() => {
        searchScheduling(search);
    }, [])


    let doSearch = (values: ISearchRequest) => {
        let request: ISearchRequest = {
            ...values
        }
        searchScheduling(request);
    }


    let searchScheduling = async (search: ISearchRequest) => {
        try {
            setLoading(true);
            let searchRequest = {
                ...search,
                dateFrom: search.dateFrom.toDate(),
                dateTo: search.dateTo.toDate()
            };
            let response = await axios.post('/api/Schedule/GetScheduling', searchRequest);
            let { item } = response.data;
            setScheduling(item);
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChange = (e: ISearchRequest) => {
        let request: ISearchRequest = {
            ...search,
            dateFrom: e.dateFrom,
            dateTo: e.dateTo,
            userNames: e.userNames,
            showEmpty: e.showEmpty
        };
        setSearch(request);
    }

    let openScheduleDetails = (event: IEventInDate, isBusy: boolean) => {
        if (isBusy) {
            setScheduleDetails(event);
        }
    }

    let closeScheduleDetails = () => {
        setScheduleDetails(null);
    }

    let dateForwardHandler = () => {
        let searchDateFrom = search.dateFrom.startOf('day');
        let searchDateTo = search.dateTo.startOf('day');
        let datesGap = searchDateTo.diff(searchDateFrom, 'days');
        let newStartDate = moment(searchDateTo);
        newStartDate.add(1, 'days');
        let newEndDate = moment(newStartDate);
        newEndDate.add(datesGap, 'days');

        let newSearch: ISearchRequest = {
            ...search,
            dateFrom: newStartDate,
            dateTo: newEndDate
        };
        handleChange(newSearch);
        doSearch(newSearch);
    }

    let dateBackHandler = () => {
        let searchDateFrom = search.dateFrom.startOf('day');
        let searchDateTo = search.dateTo.startOf('day');
        let newEndDate = moment(searchDateFrom);
        newEndDate.add(-1, 'days');
        let datesGap = searchDateTo.diff(searchDateFrom, 'days');
        let newStartDate = moment(newEndDate);
        newStartDate.add(-datesGap, 'days');

        let newSearch: ISearchRequest = {
            ...search,
            dateFrom: newStartDate,
            dateTo: newEndDate
        };
        handleChange(newSearch);
        doSearch(newSearch);
    }

    let selectWeek = () => {
        let newStartDate = moment().startOf('week').utc(true);
        let newEndDate = moment().endOf('week').utc(true);
        let newSearch: ISearchRequest = {
            ...search,
            dateFrom: newStartDate,
            dateTo: newEndDate
        };
        handleChange(newSearch);
        doSearch(newSearch);
    }

    let selectMonth = () => {
        let newStartDate = moment().startOf('month').utc(true);
        let newEndDate = moment().endOf('month').utc(true);
        let newSearch: ISearchRequest = {
            ...search,
            dateFrom: newStartDate,
            dateTo: newEndDate
        };
        handleChange(newSearch);
        doSearch(newSearch);
    }

    return (
        <Provider value={{
            search,
            scheduling,
            isLoading,
            scheduleDetails,

            doSearch,
            searchScheduling,
            handleChange,
            openScheduleDetails,
            closeScheduleDetails,
            dateBackHandler,
            dateForwardHandler,
            selectWeek,
            selectMonth
        }}>
            {children}
        </Provider>
    );
};

export default SchedulingProvider;